import { ComponentType } from "@angular/cdk/portal";
import { Injectable, inject } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from "@angular/router";

import { Observable, of } from "rxjs";
import { switchMap, take, tap } from "rxjs/operators";

import { RoleCheckConfig, UserStateService } from "../../../shared/services/user-state.service";
import { MissingRoleDialogInput } from "../components/missing-role-dialog/missing-role-dialog.component";

export interface RoleGuardConfig {
  roleGuardConfig: {
    roles: RoleCheckConfig;
    dialog?: ComponentType<any>;
    redirect?: string;
  };
}

@Injectable({ providedIn: "root" })
export class RoleGuard {
  private readonly router = inject(Router);
  private readonly userService = inject(UserStateService);
  private readonly dialogService = inject(MatDialog);

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> | Promise<boolean> | boolean {
    return this.check(route.data as RoleGuardConfig, state);
  }

  canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> | Promise<boolean> | boolean {
    return this.check(route.data as RoleGuardConfig, state);
  }

  private check(data: RoleGuardConfig | null, state: RouterStateSnapshot): Observable<boolean> {
    if (data == null || data.roleGuardConfig == null) {
      throw new Error("RoleGuard needs a RoleGuardConfig as route data!");
    }
    const config = data.roleGuardConfig;
    return this.userService.hasRoles$(config.roles).pipe(
      take(1),
      switchMap((result) =>
        !result && config.dialog
          ? this.dialogService
              .open(config.dialog, { data: { ...data, returnUrl: state.url } satisfies MissingRoleDialogInput, disableClose: true })
              .afterClosed()
          : of(result)
      ),
      tap((result) => !result && config && config.redirect && this.router.navigateByUrl(config.redirect, { replaceUrl: true }))
    );
  }
}
